/* @jsxImportSource @emotion/react */

import type { ReactNode } from 'react'
import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { CaretDownIcon } from '@slite/icons'

import { SliteLogo } from '../../Logo/SliteLogo'
import { SuperLogo } from '../../Logo/SuperLogo'
import { Menu } from '../../Menu/Menu'
import type { MenuItemProps } from '../../Menu/MenuItem'
import { MenuItem } from '../../Menu/MenuItem'
import { Popover } from '../../Popover/Popover'
import { PopoverOnHover } from '../../PopoverOnHover/PopoverOnHover'
import { mergeRefs } from '../../utils/mergeRefs'
import { useContextMenuClick } from './useContextMenuClick'

const ZINDEX_ABOVE_NAVBAR = 1000 + 1

const useDropdownMenuItems = () => {
  const intl = useIntl()
  return [
    {
      id: 'super',
      children: (
        <a href="https://super.work">
          <div className="flexbox flexbox-column flexbox-align-start flexbox-gap-8 shade-05">
            <SuperLogo variant="wordmark" height={18} />
            <div style={{ fontSize: 14 }} className="font-weight-regular">
              {intl.formatMessage({
                id: 'slite.navbar.switchMenu.superDesc',
                defaultMessage:
                  'AI search and assistants for all your team’s data.',
              })}
            </div>
          </div>
        </a>
      ),
    },
    {
      id: 'slite',
      children: (
        <a href="https://slite.com">
          <div className="flexbox flexbox-column flexbox-align-start flexbox-gap-8 shade-05">
            <SliteLogo variant="wordmark" height={18} />
            <div style={{ fontSize: 14 }} className="font-weight-regular">
              {intl.formatMessage({
                id: 'slite.navbar.switchMenu.sliteDesc',
                defaultMessage:
                  'AI-powered knowledge base for fast, effortless documentation.',
              })}
            </div>
          </div>
        </a>
      ),
    },
  ]
}

const NavbarLogoContextMenu = ({
  contextMenuPosition,
  closeContextMenu,
  contextMenuItems,
}: {
  contextMenuPosition?: { x: number; y: number } | null
  closeContextMenu: () => void
  contextMenuItems: (MenuItemProps & { children: ReactNode })[]
}) => {
  if (!contextMenuPosition) {
    return null
  }
  return (
    <Popover
      positions={['right', 'bottom']}
      align="start"
      isOpen={true}
      onClickOutside={() => closeContextMenu()}
      containerStyle={{
        zIndex: `${ZINDEX_ABOVE_NAVBAR}`,
      }}
      content={() => (
        <Menu style={{ width: 220 }} radiusSize="xlarger">
          {contextMenuItems.map((item) => (
            <MenuItem
              key={item.id}
              id={item.id}
              onClick={() => {
                if (item.onClick) {
                  item.onClick(item.id)
                }
                closeContextMenu()
              }}
            >
              {item.children}
            </MenuItem>
          ))}
        </Menu>
      )}
      target={({ ref }) => (
        <div
          ref={mergeRefs(ref)}
          className="position-absolute"
          style={{
            top: contextMenuPosition?.y,
            left: contextMenuPosition?.x,
          }}
        />
      )}
    />
  )
}

export const WebsiteNavbarLogo = ({
  contextMenuItems,
  href,
  logo,
  LinkComponent = 'a',
  disableSwitch = false,
}: {
  contextMenuItems: (MenuItemProps & { children: ReactNode })[]
  href: string
  logo: ReactNode
  LinkComponent?: React.ElementType
  disableSwitch?: boolean
}) => {
  const intl = useIntl()

  const { contextMenuPosition, onContextMenu, closeContextMenu } =
    useContextMenuClick()
  const dropdownMenuItems = useDropdownMenuItems()

  if (disableSwitch) {
    return (
      <LinkComponent
        href={href}
        className="position-relative pointer flexbox flexbox-align-center shade-05 "
        aria-label={intl.formatMessage({
          id: 'navbar.backToHomePage',
          defaultMessage: 'Back to home page',
        })}
        onContextMenu={onContextMenu}
      >
        <NavbarLogoContextMenu
          contextMenuPosition={contextMenuPosition}
          closeContextMenu={closeContextMenu}
          contextMenuItems={contextMenuItems}
        />
        {logo}
      </LinkComponent>
    )
  }

  return (
    <PopoverOnHover
      allowContentHover
      openDelay={60}
      openOnTouch
      disabled={!!contextMenuPosition}
      align={'start'}
      zIndex={`${ZINDEX_ABOVE_NAVBAR}`}
      content={({ props, ref }) => (
        <div
          className="padding-4"
          css={css`
            background: var(--alpha-shade-55);
            border: 0.5px solid var(--alpha-shade-55);
            backdrop-filter: blur(2px);
            border-radius: 20px;
            width: 280px;
          `}
          ref={ref}
          {...props}
        >
          {/* Inner card with shadow */}
          <div
            className="padding-8 background-shade-100 shade-05"
            css={css`
              box-shadow: 0px 4.02054px 8.4878px rgba(0, 0, 0, 0.101338), 0px 1.19702px 2.52704px rgba(0, 0, 0, 0.0686618), 0px 0.498106px 1.05156px rgba(0, 0, 0, 0.0477948);
              backdrop-filter: blur(4px);
              border-radius: 16px;
            `}
          >
            {dropdownMenuItems.map((item) => (
              <MenuItem
                id={item.id}
                key={item.id}
                stopPropagationOnClick
                className="padding-horizontal-16 padding-vertical-12"
              >
                {item.children}
              </MenuItem>
            ))}
          </div>
        </div>
      )}
      target={({ props: popoverTargetProps, ref, isOpen }) => (
        <div ref={ref} {...popoverTargetProps}>
          <LinkComponent
            href={href}
            className="position-relative pointer flexbox flexbox-align-center shade-05 "
            aria-label={intl.formatMessage({
              id: 'navbar.backToHomePage',
              defaultMessage: 'Back to home page',
            })}
            onContextMenu={onContextMenu}
          >
            <NavbarLogoContextMenu
              contextMenuPosition={contextMenuPosition}
              closeContextMenu={closeContextMenu}
              contextMenuItems={contextMenuItems}
            />
            {logo}
            <CaretDownIcon
              size={16}
              className={classNames(
                'transition margin-left-4',
                isOpen ? 'rotate-180' : ''
              )}
            />
          </LinkComponent>
        </div>
      )}
    />
  )
}
